import { Component, Input, OnInit, ViewChild} from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { LoadingController, ModalController } from '@ionic/angular';
import * as dayjs from 'dayjs';
import { GlobalConstants } from 'src/app/constant/constant';
import { OdometerReading } from 'src/app/entities/OdometerReading';
import { Project } from 'src/app/entities/Project';
import { TOAST_CLASS } from 'src/app/enums/notification/notification.enum';
import { DataChangeEmitWho } from 'src/app/models/data-change-emit/data-change-emit-payload';
import { OdometerReadingFormControls } from 'src/app/models/form-groups/odometer-reading-form.model';
import { DeviceInfoService } from 'src/app/services/device-info.service';
import { NotificationsService } from 'src/app/services/notifications.service';
import { OdometerReadingService } from 'src/app/services/odometer-reading.service';
import { ProjectService } from 'src/app/services/project.service';
import { TimeService } from 'src/app/services/time.service';
import { TimeRecordService } from 'src/app/services/timeRecord.service';
import { UserService } from 'src/app/services/user.service';
import { PhotoService } from 'src/app/services/photo.service';
import { MediaSource, MediaSourceSelectionService } from 'src/app/services/media-source-selection.service';
import { IonSelectSearchableComponent } from 'src/app/shared/common/ionSelectSearchable/ionSelectSearchable.component';
import { ImageSelectorUiComponent, ImagePickerPhotoVm } from 'src/app/shared/common/image-selector-ui/image-selector-ui.component';
import { DateTimeHelper } from 'src/app/utils/date-time-helper';
import { NetworkException } from 'src/app/utils/exceptions';
import { CameraSource } from '@capacitor/camera';
import { Guid } from 'src/app/constant/guid';
import { LogService } from 'src/app/baseServices/logging/log.service';
import { PendingPhoto } from 'src/app/models/photo/pending-photo';

@Component({
  selector: 'app-odometer-reading-modal',
  templateUrl: './odometer-reading-modal.component.html',
  styleUrls: ['./odometer-reading-modal.component.scss'],
})
export class OdometerReadingModalComponent implements OnInit {
  @Input() odometerReading?: OdometerReading;
  @Input() odometerReadingProject?: Project;
  @Input() date: dayjs.Dayjs;
  @Input() projects: Project[];
  @Input() allowClosure: boolean = false;
  @ViewChild('startImagePicker') startImagePickerUi: ImageSelectorUiComponent;
  @ViewChild('endImagePicker') endImagePickerUi: ImageSelectorUiComponent;

  userId: number;
  title: string;
  selectedProject?: Project;
  formGroup: FormGroup<OdometerReadingFormControls>;
  mileage: number;
  isLoading = true;

  // Photo-related properties
  startOdometerPhotoVm: ImagePickerPhotoVm<PendingPhoto> | null = null;
  endOdometerPhotoVm: ImagePickerPhotoVm<PendingPhoto> | null = null;
  isCapturingStartPhoto = false;
  isCapturingEndPhoto = false;
  hasExistingStartPhoto = false;
  hasExistingEndPhoto = false;

  sliderOptions = {
    centeredSlides: false,
    autoHeight: false,
    loop: false,
    slidesPerView: 2.4,
    coverflowEffect: {
      rotate: 50,
      stretch: 0,
      depth: 100,
      modifier: 1,
      slideShadows: true,
    },
    allowTouchMove: false,
    simulateTouch: false
  };

  constructor(
    private modalController: ModalController,
    private loadingController: LoadingController,
    private notificationsService: NotificationsService,
    private odometerReadingService: OdometerReadingService,
    private userService: UserService,
    private timeService: TimeService,
    private timeRecordService: TimeRecordService,
    private projectService: ProjectService,
    private deviceInfoService: DeviceInfoService,
    private photoService: PhotoService,
    private mediaSourceSelectionService: MediaSourceSelectionService,
    private logService: LogService
  ) {}

  async ngOnInit() {
    await this.loadData();
    this.title = this.odometerReading ? 'Edit Odometer Reading' : 'Add Odometer Reading';
    this.selectedProject = this.odometerReadingProject;
    
    const openTimeRecord = await this.timeRecordService.getOpenTimeRecord(this.userId, this.date);
    const activeProject = !!openTimeRecord ? await this.projectService.getProjectByOnlineId(openTimeRecord.ProjectId) : null;
    const activeProjectAllowsMileageTracking = !!activeProject ? await this.timeService.checkProjectAllowsMileageTracking(activeProject.Id) : false;
    const hasOpenOdometerReading = await this.timeService.checkUserHasOpenOdometerReading(this.userId, this.date);
    const isEndRequired = this.odometerReading?.OdometerEnd || !(!hasOpenOdometerReading && !!openTimeRecord && activeProjectAllowsMileageTracking);

    // Check for existing photos if editing an odometer reading
    if (this.odometerReading?.RecordId) {
      await this.loadExistingPhotos();
    }

    this.formGroup = new FormGroup<OdometerReadingFormControls>(
      {
        project: new FormControl(this.odometerReadingProject ? this.odometerReadingProject : null, [Validators.required]),
        startValue: new FormControl(this.odometerReading?.OdometerStart ?? null, [Validators.required, Validators.min(0)]),
        endValue: new FormControl(this.odometerReading?.OdometerEnd ?? null, 
          isEndRequired ? [Validators.required, Validators.min(0)] : [Validators.min(0)]
        ),
      },
      { validators: [this.validateStartAndEndMileage.bind(this)], asyncValidators: [this.validateOdometerReadingHasNoOverlap.bind(this)] }
    );
    this.calculateMileage();
    this.formGroup.valueChanges.subscribe(() => this.calculateMileage());
    this.updateEndValueDisabledState();
    this.isLoading = false;
  }

  async loadData() {
    this.userId = this.odometerReading?.UserId ?? await this.userService.getCurrentUserId();
  }

  private async loadExistingPhotos(): Promise<void> {
    // Check if photos exist based on blob names in the OdometerReading entity
    // Media records are cleared on login, but blob names persist
    
    // Refresh the odometer reading from the database to get the latest photo blob names
    const refreshedReading = await this.odometerReadingService.getOdometerReadingByRecordId(this.odometerReading.RecordId);
    if (refreshedReading) {
      this.odometerReading = refreshedReading;
    }
    
    this.hasExistingStartPhoto = !!this.odometerReading.StartPhotoBlobName;
    this.hasExistingEndPhoto = !!this.odometerReading.EndPhotoBlobName;
  }

  private updateEndValueDisabledState(): void {
    const ctrl = this.formGroup.get('endValue')!;

    const shouldDisable = !!this.odometerReading
      && this.odometerReading.OdometerEnd == null
      && !this.allowClosure;

    if (shouldDisable && ctrl.enabled) {
      ctrl.disable({ emitEvent: false });
    } else if (!shouldDisable && ctrl.disabled) {
      ctrl.enable({ emitEvent: false });
    }
  }

  private calculateMileage() {
    this.mileage = this.formGroup.controls.endValue.value && this.formGroup.controls.startValue.value ? this.formGroup.controls.endValue.value - this.formGroup.controls.startValue.value : null;
  }

  private validateStartAndEndMileage(fb: FormGroup) {
    const startValue = fb.controls.startValue.value;
    const endValue = fb.controls.endValue.value;

    if (startValue === null || endValue === null) {
      return null;
    }

    const odometerReading = new OdometerReading({
      OdometerStart: startValue,
      OdometerEnd: endValue,
      UserId: this.userId,
      Date: DateTimeHelper.formatDateTimeAsDate(this.date)
    });

    const isValid = this.odometerReadingService.checkIsEndMilageGreaterOrEqualStartMilage(odometerReading);
    return isValid ? null : { rangeError: true };
  }

  private async validateOdometerReadingHasNoOverlap(fb: FormGroup) {
    const odometerReading = new OdometerReading({
      ...this.odometerReading,
      OdometerStart: fb.controls.startValue.value,
      OdometerEnd: fb.controls.endValue.value,
      UserId: this.userId,
      Date: DateTimeHelper.formatDateTimeAsDate(this.date)
    });

    const overlaps = await this.odometerReadingService.getOverlappingOdometerReadings(odometerReading);
    return overlaps.length ? { overlap: true } : null;
  }

  getValidationErrors(control: AbstractControl): string[] {
    if (!control.errors) {
      return [];
    }

    return Object.keys(control.errors)
      .map(key => {
        switch (key) {
          case 'required':
            return 'This field is required';
          case 'min':
            return `Reading must be greater than or equal to ${control.errors[key].min}`;
          default:
            return 'Unknown error';
        }
      })
      .filter(error => error !== '');
  }

  getFormGroupErrors(): string[] {
    if (!this.formGroup.errors) {
      return [];
    }

    return Object.keys(this.formGroup.errors)
      .map(key => {
        if (!this.formGroup.touched) {
          return ''; // Do not show required error if the control is not touched
        }

        switch (key) {
          case 'rangeError':
            return 'Ending odometer reading must be greater than or equal to starting odometer reading';
          case 'overlap':
            return 'Your odometer reading overlaps with another reading for today';
          default:
            return 'Unknown error';
        }
      })
      .filter(error => error !== '');
  }

  async openProjectModal() {
    const modal = await this.modalController.create({
      component: IonSelectSearchableComponent,
      componentProps: {
        value: this.projects,
        displayProperty: 'Name',
        showSearchBoxCountGreaterThan: GlobalConstants.SEARCH_LIST_ITEM_DISPLAY_LIMIT,
      },
      cssClass: 'dropdown-options-modal',
    });

    await modal.present();
    const { data } = await modal.onWillDismiss();
    if (data) {
      this.selectedProject = data;
      this.formGroup.controls.project.setValue(this.selectedProject);

      this.formGroup.updateValueAndValidity();
    }
  }

  // #region Photo handling methods

  get shouldShowStartPhotoInput(): boolean {
    return !this.hasExistingStartPhoto;
  }

  get shouldShowEndPhotoInput(): boolean {
    const endValueDisabled = this.formGroup?.get('endValue')?.disabled ?? false;
    return !this.hasExistingEndPhoto && !endValueDisabled;
  }

  async onStartOdometerPhotoCaptureRequested() {
    if (this.isCapturingStartPhoto) {
      return; // Prevent concurrent capture operations
    }

    const selectedSource = await this.mediaSourceSelectionService.promptForSelection({
      header: this.startOdometerPhotoVm ? 'Retake Photo' : 'Add Photo',
      cameraText: 'Take Photo',
      galleryText: 'Select from Gallery',
    });

    if (!selectedSource) return;

    const cameraSource = selectedSource === MediaSource.Camera ? CameraSource.Camera : CameraSource.Photos;

    this.isCapturingStartPhoto = true;
    
    try {
      const captureResult = await this.photoService.addNewToGallery(
        cameraSource,
        { width: GlobalConstants.DEFAULT_CANVAS_WIDTH, height: GlobalConstants.DEFAULT_CANVAS_HEIGHT },
        undefined
      );

      if (!captureResult?.capturedPhoto?.dataUrl) {
        this.logService.error('OdometerModal.StartPhotoCaptureFailedNoData', 'Photo capture failed: No data URL available');
        return;
      }

      const newPhoto: PendingPhoto = {
        dataUrl: captureResult.capturedPhoto.dataUrl,
        format: captureResult.capturedPhoto.format,
        fileName: 'File' + Guid.newGuid() + '.' + captureResult.capturedPhoto.format,
        title: 'Start Odometer Photo',
      };

      this.startOdometerPhotoVm = {
        meta: newPhoto,
        title: newPhoto.title ?? 'Start Odometer Photo',
        fileName: newPhoto.fileName,
        src: captureResult.capturedPhoto.dataUrl,
      };
    } catch (error) {
      this.logService.error('OdometerModal.StartPhotoCaptureError', error);
    } finally {
      this.isCapturingStartPhoto = false;
    }
  }

  async onEndOdometerPhotoCaptureRequested() {
    if (this.isCapturingEndPhoto) {
      return; // Prevent concurrent capture operations
    }

    const selectedSource = await this.mediaSourceSelectionService.promptForSelection({
      header: this.endOdometerPhotoVm ? 'Retake Photo' : 'Add Photo',
      cameraText: 'Take Photo',
      galleryText: 'Select from Gallery',
    });

    if (!selectedSource) return;

    const cameraSource = selectedSource === MediaSource.Camera ? CameraSource.Camera : CameraSource.Photos;

    this.isCapturingEndPhoto = true;
    
    try {
      const captureResult = await this.photoService.addNewToGallery(
        cameraSource,
        { width: GlobalConstants.DEFAULT_CANVAS_WIDTH, height: GlobalConstants.DEFAULT_CANVAS_HEIGHT },
        undefined
      );

      if (!captureResult?.capturedPhoto?.dataUrl) {
        this.logService.error('OdometerModal.EndPhotoCaptureFailedNoData', 'Photo capture failed: No data URL available');
        return;
      }

      const newPhoto: PendingPhoto = {
        dataUrl: captureResult.capturedPhoto.dataUrl,
        format: captureResult.capturedPhoto.format,
        fileName: 'File' + Guid.newGuid() + '.' + captureResult.capturedPhoto.format,
        title: 'End Odometer Photo',
      };

      this.endOdometerPhotoVm = {
        meta: newPhoto,
        title: newPhoto.title ?? 'End Odometer Photo',
        fileName: newPhoto.fileName,
        src: captureResult.capturedPhoto.dataUrl,
      };
    } catch (error) {
      this.logService.error('OdometerModal.EndPhotoCaptureError', error);
    } finally {
      this.isCapturingEndPhoto = false;
    }
  }

  onStartOdometerPhotoRemoveRequested(photoVm: ImagePickerPhotoVm<PendingPhoto>, event: Event) {
    event.stopPropagation();
    this.clearStartOdometerPhoto();
    void this.startImagePickerUi?.refreshSlides();
  }

  onEndOdometerPhotoRemoveRequested(photoVm: ImagePickerPhotoVm<PendingPhoto>, event: Event) {
    event.stopPropagation();
    this.clearEndOdometerPhoto();
    void this.endImagePickerUi?.refreshSlides();
  }

  private clearStartOdometerPhoto() {
    this.startOdometerPhotoVm = null;
  }

  private clearEndOdometerPhoto() {
    this.endOdometerPhotoVm = null;
  }

  // #endregion

  async onSubmit() {
    if (this.odometerReading) {
      await this.updateOdometerReading();
    } else {
      await this.createOdometerReading();
    }
  }

  async createOdometerReading() {
    const loading = await this.loadingController.create({ message: 'Creating odometer reading...' });
    try {
      await loading.present();
      
      await this.timeService.createOdometerReading(
        DataChangeEmitWho.Self,
        {
          OdometerStart: this.formGroup.controls.startValue.value,
          OdometerEnd: this.formGroup.controls.endValue.value,
          ProjectId: this.selectedProject.Id,
          UserId: this.userId,
          Date: this.date,
          StartOdometerPhoto: this.startOdometerPhotoVm?.meta ?? null,
          EndOdometerPhoto: this.endOdometerPhotoVm?.meta ?? null,
        }
      );

      await this.timeService.syncTimeData();
      this.onClose();
    } catch (e) {
      if (e instanceof NetworkException) {
        this.notificationsService.showToast({ message: 'Your change has been made locally. Please connect to Internet to upload this change.', cssClass: TOAST_CLASS.WARNING });
        this.onClose();
      } else {
        console.log(e);
        this.notificationsService.showToast({ message: 'An error occurred. ' + e, cssClass: TOAST_CLASS.DANGER });
      }
    } finally {
      loading.dismiss();
    }
  }

  async updateOdometerReading() {
    const loading = await this.loadingController.create({ message: 'Updating odometer reading...' });
    try {
      await loading.present();

      await this.timeService.updateOdometerReading(
        DataChangeEmitWho.Self,
        this.odometerReading.RecordId,
        {
          OdometerStart: this.formGroup.controls.startValue.value,
          OdometerEnd: this.formGroup.controls.endValue.value,
          ProjectId: this.selectedProject.Id,
          StartOdometerPhoto: this.startOdometerPhotoVm?.meta ?? null,
          EndOdometerPhoto: this.endOdometerPhotoVm?.meta ?? null,
        },
        false,
        this.odometerReading
      );

      await this.timeService.syncTimeData();
      this.onClose();
    } catch (e) {
      if (e instanceof NetworkException) {
        this.notificationsService.showToast({ message: 'Your change has been made locally. Please connect to Internet to upload this change.', cssClass: TOAST_CLASS.WARNING });
        this.onClose();
      } else {
        console.log(e);
        this.notificationsService.showToast({ message: 'An error occurred. ' + e, cssClass: TOAST_CLASS.DANGER });
      }
    } finally {
      loading.dismiss();
    }
  }

  async onDelete() {
    const loading = await this.loadingController.create({ message: 'Deleting odometer reading...' });
    try {
      await loading.present();
      const inputType = await this.deviceInfoService.getInputType();

      await this.timeService.deleteOdometerReading(
        DataChangeEmitWho.Self,
        this.odometerReading.RecordId,
        {
          InputType: inputType
        },
        false,
        this.odometerReading
      );

      await this.timeService.syncTimeData();
      this.onClose();
    } catch (e) {
      if (e instanceof NetworkException) {
        this.notificationsService.showToast({ message: 'Your change has been made locally. Please connect to Internet to upload this change.', cssClass: TOAST_CLASS.WARNING });
        this.onClose();
      } else {
        console.log(e);
        this.notificationsService.showToast({ message: 'An error occurred. ' + e, cssClass: TOAST_CLASS.DANGER });
      }
    } finally {
      loading.dismiss();
    }
  }

  onClose() {
    this.modalController.dismiss();
  }
}
